import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  ICustomizationAsset,
  ICustomizationAssetInput,
  ICustomizationAssetsPage,
  IUserCustomization,
  CustomizationAssetType,
} from '../../@core/interfaces/customization.interface';

@Injectable({ providedIn: 'root' })
export class CustomizationService {
  private readonly url = `${environment.GATEWAY_URL}/profile/customization`;

  constructor(private readonly http: HttpClient) {}

  getAssets(filters: {
    type?: CustomizationAssetType;
    isEnabled?: boolean;
    requiresGrant?: boolean;
    pagination?: { limit: number; offset: number };
  }): Observable<ICustomizationAssetsPage> {
    return this.http.post<ICustomizationAssetsPage>(`${this.url}/assets/get`, filters, { withCredentials: true });
  }

  createAsset(input: ICustomizationAssetInput & { type: CustomizationAssetType }): Observable<ICustomizationAsset> {
    return this.http.post<ICustomizationAsset>(`${this.url}/assets`, input, { withCredentials: true });
  }

  updateAsset(id: number, input: ICustomizationAssetInput): Observable<ICustomizationAsset> {
    const body = { ...input };
    delete body.type;
    return this.http.patch<ICustomizationAsset>(`${this.url}/assets/${id}`, body, { withCredentials: true });
  }

  deleteAsset(id: number): Observable<void> {
    return this.http.delete<void>(`${this.url}/assets/${id}`, { withCredentials: true });
  }

  uploadImage(id: number, file: File): Observable<ICustomizationAsset> {
    const body = new FormData();
    body.append('file', file);
    return this.http.post<ICustomizationAsset>(`${this.url}/assets/${id}/image`, body, { withCredentials: true });
  }

  getUserCustomization(userId: number): Observable<IUserCustomization> {
    return this.http.get<IUserCustomization>(`${this.url}/users/${userId}`, { withCredentials: true });
  }

  setUserCustomization(
    userId: number,
    customization: Array<{ type: CustomizationAssetType; assetId: number | null }>,
  ): Observable<void> {
    return this.http.patch<void>(`${this.url}/users/${userId}`, { customization }, { withCredentials: true });
  }

  grantAsset(userId: number, assetId: number, sourceRef?: string): Observable<void> {
    return this.http.post<void>(
      `${this.url}/users/${userId}/grants`,
      { assetId, sourceRef: sourceRef || null },
      { withCredentials: true },
    );
  }

  revokeAsset(userId: number, assetId: number): Observable<void> {
    return this.http.delete<void>(`${this.url}/users/${userId}/grants/${assetId}`, { withCredentials: true });
  }
}
